"use client";

import React from "react";
import { Terminal, ChevronRight, CircleAlert } from "lucide-react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Card, CardContent } from "@/components/ui/card";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
function InnovationInsightsConsumer() {
  const {
    posts,
    loading,
    error
  } = usePosts();
  if (loading) {
    return <div className="flex w-full space-x-6 overflow-hidden py-8">
        {[...Array(4)].map((_, i) => <Card key={i} data-index={i} className="w-[320px] shrink-0 border-border/40 bg-card/50">
            <div className="h-48 animate-pulse bg-muted/20" />
            <CardContent className="p-6">
              <div className="mb-4 h-4 w-24 animate-pulse bg-muted/20" />
              <div className="mb-2 h-6 w-full animate-pulse bg-muted/20" />
              <div className="h-6 w-2/3 animate-pulse bg-muted/20" />
            </CardContent>
          </Card>)}
      </div>;
  }
  if (error) {
    return <div className="flex flex-col items-center justify-center py-16 text-center">
        <CircleAlert className="mb-4 size-8 text-destructive" />
        <p className="font-mono text-sm text-muted-foreground">ERR_FETCH_FAILED: {error}</p>
      </div>;
  }
  if (posts.length === 0) {
    return <div className="flex flex-col items-center justify-center py-16 text-center">
        <Terminal className="mb-4 size-8 text-muted-foreground" />
        <p className="font-mono text-sm text-muted-foreground">NO_DATA_FOUND</p>
      </div>;
  }
  return <ScrollArea className="w-full whitespace-nowrap pb-8">
      <div className="flex w-max space-x-6 px-1 py-4">
        {posts.map((post, i) => <Card key={post.id} data-index={i} className="w-[320px] shrink-0 overflow-hidden rounded-none border border-border/50 bg-black/40 shadow-lg md:w-[400px]">
            {}
            <div className="flex h-8 items-center border-b border-border/50 bg-muted/10 px-3">
              <div className="mr-2 size-2.5 rounded-full bg-destructive/80" />
              <div className="mr-2 size-2.5 rounded-full bg-chart-4/80" />
              <div className="size-2.5 rounded-full bg-primary/80" />
              <span className="ml-auto font-mono text-[10px] text-muted-foreground">
                tty1
              </span>
            </div>

            {}
            {post.featuredImage ? <div className="relative h-48 w-full overflow-hidden border-b border-border/50 bg-muted/20">
                <img src={post.featuredImage} alt={post.title} className="size-full object-cover contrast-125 grayscale" loading="lazy" />
                <div className="absolute inset-0 bg-primary/5 mix-blend-overlay" />
              </div> : <div className="flex h-48 w-full items-center justify-center border-b border-border/50 bg-muted/10">
                <Terminal className="size-12 text-muted-foreground/30" />
              </div>}

            <CardContent className="whitespace-normal p-6">
              <div className="mb-4 flex items-center justify-between">
                <span className="font-mono text-xs text-primary">
                  {post.date ? new Date(post.date).toISOString().split('T')[0] : 'SYS.DATE'}
                </span>
                {post.categories && post.categories.length > 0 && <span className="border border-primary/30 bg-primary/5 px-2 py-0.5 font-mono text-[10px] uppercase text-primary/80">
                    {post.categories[0].name}
                  </span>}
              </div>
              
              <Link to={post.link || "#"}>
                <h3 className="mb-6 line-clamp-2 text-lg font-semibold leading-tight tracking-tight text-foreground">
                  {post.title}
                </h3>
              </Link>

              <Link to={post.link || "#"} className="inline-block">
                <Button variant="outline" size="sm" className="h-8 rounded-none border-primary/30 font-mono text-xs text-primary hover:bg-primary/10 hover:text-primary">
                  [EXECUTE_READ]
                  <ChevronRight className="ml-1 size-3" />
                </Button>
              </Link>
            </CardContent>
          </Card>)}
      </div>
      <ScrollBar orientation="horizontal" className="bg-muted/20" />
    </ScrollArea>;
}
export default function InnovationInsights() {
  const wp_query_params = {
    per_page: 6,
    orderby: "date",
    order: "desc"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="innovation-insights" className="dark bg-background py-24 text-foreground">
      <div className="container mx-auto px-4 md:px-6">
        <div className="mb-12 flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
          <div className="max-w-2xl">
            <div className="mb-4 flex items-center gap-2">
              <Terminal className="size-5 text-primary" />
              <span className="font-mono text-sm uppercase tracking-wider text-primary">Blog</span>
            </div>
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl md:text-5xl">
              Innovation Insights
            </h2>
            <p className="mt-4 font-mono text-sm text-muted-foreground">
              &gt; Fetching latest telemetry from engineering and research divisions...
            </p>
          </div>
          
          <Link to="/SupportPage">
            <Button variant="ghost" className="font-mono text-sm text-muted-foreground hover:text-primary">
              VIEW_ALL_LOGS <ChevronRight className="ml-2 size-4" />
            </Button>
          </Link>
        </div>

        <PostsProvider wp_query={{
        per_page: 6,
        orderby: "date",
        order: "desc"
      }} className="w-full">
          <InnovationInsightsConsumer />
        </PostsProvider>
      </div>
    </section>;
}